import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import api, { API_BASE_URL } from '../api/axios'
import { getStreamAuthToken } from '../auth/streamAuth'
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  TrashIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  PauseIcon,
} from '@heroicons/react/24/outline'

interface LogFile {
  name: string
  sizeKB: number
  modified: string
  type: 'combined' | 'error'
}

interface LogStats {
  errors: number
  warnings: number
  info: number
  debug: number
  total: number
  fileSizeKB: number
  errorFileSizeKB: number
}

interface ParsedLog {
  raw: string
  timestamp: string | null
  level: string          // lowercased: error | warn | info | debug
  context: string        // e.g. APP, AUTH, TENANT:kelly
  tenant: string | null  // parsed tenant code from context, if any
  message: string
  data: Record<string, unknown> | null
  stack: string | null   // continuation / stack-trace lines attached to this entry
}

const LEVEL_TEXT: Record<string, string> = {
  error: 'text-red-400',
  warn: 'text-amber-400',
  info: 'text-sky-400',
  debug: 'text-slate-400',
}

const LEVEL_CHIP_ON: Record<string, string> = {
  error: 'bg-red-950/60 text-red-300 border-red-800',
  warn: 'bg-amber-950/50 text-amber-300 border-amber-800',
  info: 'bg-sky-950/50 text-sky-300 border-sky-800',
  debug: 'bg-slate-800 text-slate-300 border-slate-600',
}

const LEVEL_CHIP_OFF = 'bg-slate-900 text-slate-600 border-slate-800'

const ALL_LEVELS = ['error', 'warn', 'info', 'debug'] as const

// Mirror of backend logger format:  [timestamp] [LEVEL] [context] message {json}
const LINE_RE = /^\[([^\]]+)\]\s+\[([A-Z]+)\]\s+\[([^\]]+)\]\s+([\s\S]*)$/

// Split a trailing {...} JSON object off the message (guarded parse).
function splitTrailingJson(message: string): { message: string; data: Record<string, unknown> | null } {
  const trimmed = message.trimEnd()
  const start = trimmed.lastIndexOf('{')
  if (start === -1 || !trimmed.endsWith('}')) return { message, data: null }
  const candidate = trimmed.slice(start)
  try {
    const parsed = JSON.parse(candidate)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { message: trimmed.slice(0, start).trimEnd(), data: parsed as Record<string, unknown> }
    }
  } catch {
    /* not valid JSON — leave message intact */
  }
  return { message, data: null }
}

function tenantFromContext(context: string): string | null {
  const m = context.match(/^TENANT:(.+)$/i)
  return m ? m[1] : null
}

// Parse a single raw log line. Returns null for continuation/stack lines
// that do not match the logger format (so they can attach to the prior entry).
function parseLogLine(raw: string): ParsedLog | null {
  const m = raw.match(LINE_RE)
  if (!m) return null
  const [, timestamp, levelRaw, context, rest] = m
  const { message, data } = splitTrailingJson(rest)
  return {
    raw,
    timestamp,
    level: levelRaw.toLowerCase(),
    context,
    tenant: tenantFromContext(context),
    message,
    data,
    stack: null,
  }
}

// Turn a flat array of raw lines into structured entries, attaching
// non-matching continuation lines (stack traces) to the preceding entry.
function buildEntries(rawLines: string[]): ParsedLog[] {
  const entries: ParsedLog[] = []
  for (const raw of rawLines) {
    if (!raw.trim()) continue
    const parsed = parseLogLine(raw)
    if (parsed) {
      entries.push(parsed)
    } else if (entries.length > 0) {
      const prev = entries[entries.length - 1]
      prev.stack = prev.stack ? `${prev.stack}\n${raw}` : raw
    } else {
      // Orphan line with no owner — synthesize a bare info entry so it shows.
      entries.push({
        raw,
        timestamp: null,
        level: 'info',
        context: '',
        tenant: null,
        message: raw,
        data: null,
        stack: null,
      })
    }
  }
  return entries
}

const TIME_WINDOWS: Record<string, number> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  all: 0,
}

export default function ServerLogs() {
  const [files, setFiles] = useState<LogFile[]>([])
  const [stats, setStats] = useState<LogStats | null>(null)
  const [selectedFile, setSelectedFile] = useState<string>('')
  const [entries, setEntries] = useState<ParsedLog[]>([])
  const [searchResults, setSearchResults] = useState<ParsedLog[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [activeSearch, setActiveSearch] = useState('') // last submitted search term
  const [tailLines, setTailLines] = useState(200)
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)

  // Combinable filters
  const [activeLevels, setActiveLevels] = useState<Set<string>>(
    () => new Set(['error', 'warn', 'info']),
  )
  const [tenantFilter, setTenantFilter] = useState<string>('all')
  const [timeWindow, setTimeWindow] = useState<string>('all')

  // Live tail
  const [live, setLive] = useState(false)
  const [autoScroll, setAutoScroll] = useState(true)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  // Cleanup
  const [cleanDays, setCleanDays] = useState(14)
  const [cleanConfirm, setCleanConfirm] = useState(false)

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Holds the AbortController for the active fetch-based SSE stream (replaces old EventSource ref)
  const abortCtrlRef = useRef<AbortController | null>(null)
  const logEndRef = useRef<HTMLDivElement>(null)
  const selectedFileRef = useRef('')
  selectedFileRef.current = selectedFile

  useEffect(() => {
    fetchFiles()
    fetchStats()
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (abortCtrlRef.current) abortCtrlRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchFiles = async () => {
    try {
      const res = await api.get('/super-admin/logs/list')
      const list: LogFile[] = res.data.files || []
      setFiles(list)
      if (!selectedFileRef.current) {
        const todayCombined = list.find((f) => f.name.startsWith('combined'))
        if (todayCombined) {
          setSelectedFile(todayCombined.name)
          tailFile(todayCombined.name, tailLines)
        }
      }
    } catch {
      /* silent */
    }
  }

  const fetchStats = async () => {
    try {
      const res = await api.get('/super-admin/logs/stats')
      setStats(res.data.stats)
    } catch {
      /* silent */
    }
  }

  const tailFile = useCallback(async (file: string, count: number) => {
    if (!file) return
    setLoading(true)
    try {
      const res = await api.get('/super-admin/logs/tail', { params: { file, lines: count } })
      setEntries(buildEntries(res.data.lines || []))
    } catch {
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [])

  const doSearch = async (term: string) => {
    setActiveSearch(term)
    if (!term.trim()) {
      setSearchResults([])
      return
    }
    setSearching(true)
    try {
      const res = await api.get('/super-admin/logs/search', {
        params: { q: term, file: selectedFile || undefined, lines: 500 },
      })
      const raws: string[] = (res.data.results || []).map((r: { raw: string }) => r.raw)
      setSearchResults(buildEntries(raws))
    } catch {
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }

  const doCleanup = async () => {
    try {
      await api.delete('/super-admin/logs/cleanup', { params: { days: cleanDays } })
      setCleanConfirm(false)
      fetchFiles()
      fetchStats()
    } catch {
      /* silent */
    }
  }

  // ---- Live tail: header-authenticated fetch stream with polling fallback ----
  const stopLive = useCallback(() => {
    if (abortCtrlRef.current) {
      abortCtrlRef.current.abort()
      abortCtrlRef.current = null
    }
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const startPollingFallback = useCallback(
    (file: string) => {
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = setInterval(() => {
        if (selectedFileRef.current === file) tailFile(file, tailLines)
      }, 5000)
    },
    [tailFile, tailLines],
  )

  const startLive = useCallback(
    async (file: string) => {
      stopLive()
      if (!file) return

      // Send the bearer token in Authorization so the super-admin middleware
      // can verify it. No token in the URL. Better Auth first, OIDC as a
      // fallback — without that order a Better Auth operator streams as a
      // stale OIDC identity, or 401s and silently drops back to polling.
      const headers: Record<string, string> = {}
      const token = await getStreamAuthToken()
      if (token) headers['Authorization'] = `Bearer ${token}`

      const ctrl = new AbortController()
      abortCtrlRef.current = ctrl

      const url = `${API_BASE_URL}/super-admin/logs/tail/stream?file=${encodeURIComponent(file)}`

      try {
        const res = await fetch(url, {
          headers,
          credentials: 'include',
          signal: ctrl.signal,
        })

        if (!res.ok || !res.body) {
          // Server returned an error (e.g. 401/404) — fall back to polling
          abortCtrlRef.current = null
          startPollingFallback(file)
          return
        }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        const processChunk = (chunk: string) => {
          // SSE frames are separated by \n\n; each line inside a frame may be
          // "data: <content>" or ": ping" (heartbeat comment).
          buffer += chunk
          const frames = buffer.split('\n\n')
          buffer = frames.pop() ?? '' // last (potentially incomplete) frame stays in buffer
          for (const frame of frames) {
            for (const line of frame.split('\n')) {
              if (!line.startsWith('data: ')) continue
              const raw = line.slice(6) // strip "data: " prefix
              if (!raw.trim()) continue
              const parsed = parseLogLine(raw)
              setEntries((prev) => {
                if (parsed) return [...prev, parsed]
                // continuation / stack line — attach to last entry
                if (prev.length === 0) return prev
                const next = prev.slice()
                const last = { ...next[next.length - 1] }
                last.stack = last.stack ? `${last.stack}\n${raw}` : raw
                next[next.length - 1] = last
                return next
              })
            }
          }
        }

        // Drain the stream until aborted or closed by server
        ;(async () => {
          try {
            while (true) {
              const { value, done } = await reader.read()
              if (done) break
              processChunk(decoder.decode(value, { stream: true }))
            }
          } catch (err: unknown) {
            // AbortError is expected on Pause / unmount — don't fall back to poll
            if (err instanceof Error && err.name === 'AbortError') return
            // Any other stream error degrades to polling
            if (abortCtrlRef.current) {
              abortCtrlRef.current = null
              startPollingFallback(file)
            }
          }
        })()
      } catch (err: unknown) {
        // fetch() itself failed (network error, AbortError on immediate abort)
        if (err instanceof Error && err.name === 'AbortError') return
        abortCtrlRef.current = null
        startPollingFallback(file)
      }
    },
    [stopLive, startPollingFallback],
  )

  // React to Live toggle / file change
  useEffect(() => {
    if (live && selectedFile) {
      startLive(selectedFile)
    } else {
      stopLive()
    }
    return () => stopLive()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, selectedFile])

  // Autoscroll when entries change and autoscroll is enabled
  useEffect(() => {
    if (autoScroll) {
      const id = setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 30)
      return () => clearTimeout(id)
    }
  }, [entries, searchResults, autoScroll])

  const handleFileChange = (name: string) => {
    setSelectedFile(name)
    setEntries([])
    setSearchResults([])
    setExpanded(new Set())
    if (activeSearch.trim()) {
      doSearch(activeSearch)
    } else {
      tailFile(name, tailLines)
    }
  }

  const toggleLevel = (lvl: string) => {
    setActiveLevels((prev) => {
      const next = new Set(prev)
      if (next.has(lvl)) next.delete(lvl)
      else next.add(lvl)
      return next
    })
  }

  const toggleExpand = (i: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }

  // Source of truth for the list: search results if a search is active, else tail entries.
  const sourceEntries = activeSearch.trim() ? searchResults : entries

  // Tenant options derived from parsed [context] tags across the loaded set.
  const tenantOptions = useMemo(() => {
    const set = new Set<string>()
    for (const e of sourceEntries) if (e.tenant) set.add(e.tenant)
    return Array.from(set).sort()
  }, [sourceEntries])

  // Apply combinable client-side filters: level + tenant + time window + full-text.
  const displayEntries = useMemo(() => {
    const now = Date.now()
    const windowMs = TIME_WINDOWS[timeWindow] ?? 0
    const q = searchQuery.trim().toLowerCase()
    return sourceEntries.filter((e) => {
      if (!activeLevels.has(e.level)) return false
      if (tenantFilter !== 'all' && e.tenant !== tenantFilter) return false
      if (windowMs > 0 && e.timestamp) {
        const t = Date.parse(e.timestamp)
        if (!Number.isNaN(t) && now - t > windowMs) return false
      }
      // Live full-text refinement (applies on top of the server search too).
      if (q && !e.raw.toLowerCase().includes(q)) return false
      return true
    })
  }, [sourceEntries, activeLevels, tenantFilter, timeWindow, searchQuery])

  const windowCounts = useMemo(() => {
    let err = 0
    let warn = 0
    for (const e of displayEntries) {
      if (e.level === 'error') err++
      else if (e.level === 'warn') warn++
    }
    return { err, warn }
  }, [displayEntries])

  const exportLines = () => {
    if (displayEntries.length === 0) return
    const text = displayEntries.map((l) => l.raw).join('\n')
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const stamp = new Date().toISOString().replace(/[:.]/g, '-')
    a.href = url
    a.download = `${selectedFile || 'logs'}-${stamp}.log`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const fmtTime = (ts: string | null) => {
    if (!ts) return ''
    const d = new Date(ts)
    if (Number.isNaN(d.getTime())) return ts
    return d.toLocaleTimeString('en-US', { hour12: false }) + 'Z'
  }

  return (
    <div className="p-6 space-y-5 min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-500">System / Server Logs</div>
          <h1 className="text-xl font-bold text-white mt-0.5">Server Logs</h1>
        </div>
        <button
          onClick={() => {
            fetchFiles()
            fetchStats()
            if (activeSearch.trim()) doSearch(activeSearch)
            else if (selectedFile) tailFile(selectedFile, tailLines)
          }}
          className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm text-slate-300 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Errors', value: stats.errors, color: 'text-red-400', bg: 'bg-red-950/50 border-red-900/50' },
            { label: 'Warnings', value: stats.warnings, color: 'text-amber-400', bg: 'bg-amber-950/50 border-amber-900/50' },
            { label: 'Info', value: stats.info, color: 'text-sky-400', bg: 'bg-sky-950/50 border-sky-900/50' },
            { label: 'Debug', value: stats.debug, color: 'text-slate-400', bg: 'bg-slate-800/50 border-slate-700/50' },
            { label: 'Total', value: stats.total, color: 'text-white', bg: 'bg-slate-800/50 border-slate-700/50' },
          ].map((s) => (
            <div key={s.label} className={`rounded-xl border p-3 ${s.bg}`}>
              <div className={`text-xl font-bold ${s.color}`}>{s.value.toLocaleString()}</div>
              <div className="text-xs text-slate-500 mt-0.5">{s.label} today</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* File list sidebar */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Log Files</div>
          {files.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">
              No log files yet.
              <br />
              They'll appear after the server logs something.
            </p>
          )}
          {files.map((f) => (
            <button
              key={f.name}
              onClick={() => handleFileChange(f.name)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                selectedFile === f.name ? 'bg-blue-600 text-white' : 'hover:bg-slate-800 text-slate-300'
              }`}
            >
              <span className="truncate font-mono text-xs block">{f.name}</span>
              <span className="mt-1 text-xs opacity-60 block">{f.sizeKB} KB</span>
            </button>
          ))}

          {/* Cleanup */}
          <div className="pt-4 border-t border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Cleanup</div>
            {cleanConfirm ? (
              <div className="space-y-2">
                <p className="text-xs text-amber-400">Delete files older than {cleanDays} days?</p>
                <div className="flex gap-2">
                  <button onClick={doCleanup} className="flex-1 py-1.5 bg-red-600 hover:bg-red-700 rounded text-xs text-white font-semibold">
                    Delete
                  </button>
                  <button onClick={() => setCleanConfirm(false)} className="flex-1 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-xs text-slate-300">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <select
                  value={cleanDays}
                  onChange={(e) => setCleanDays(Number(e.target.value))}
                  className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-300"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
                <button
                  onClick={() => setCleanConfirm(true)}
                  className="p-2 rounded bg-slate-800 hover:bg-red-900/40 text-slate-400 hover:text-red-400 transition-colors"
                  title="Clean up old logs"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main log viewer */}
        <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl flex flex-col overflow-hidden">
          {/* Filter bar */}
          <div className="p-3 border-b border-slate-800 flex flex-wrap items-center gap-2">
            {/* Source (line count) */}
            <div className="relative">
              <select
                value={tailLines}
                onChange={(e) => {
                  const n = Number(e.target.value)
                  setTailLines(n)
                  if (!activeSearch.trim() && selectedFile) tailFile(selectedFile, n)
                }}
                className="appearance-none bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg pl-3 pr-7 py-1.5 cursor-pointer"
              >
                <option value={50}>50 lines</option>
                <option value={100}>100 lines</option>
                <option value={200}>200 lines</option>
                <option value={500}>500 lines</option>
                <option value={1000}>1000 lines</option>
              </select>
              <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
            </div>

            {/* Level chips */}
            <div className="flex gap-1.5">
              {ALL_LEVELS.map((lvl) => {
                const on = activeLevels.has(lvl)
                return (
                  <button
                    key={lvl}
                    onClick={() => toggleLevel(lvl)}
                    className={`text-[10.5px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border transition-colors ${
                      on ? LEVEL_CHIP_ON[lvl] : LEVEL_CHIP_OFF
                    }`}
                  >
                    {lvl}
                  </button>
                )
              })}
            </div>

            {/* Tenant filter */}
            <div className="relative">
              <select
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
                className="appearance-none bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg pl-3 pr-7 py-1.5 cursor-pointer"
              >
                <option value="all">Tenant: All</option>
                {tenantOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
            </div>

            {/* Time window */}
            <div className="relative">
              <select
                value={timeWindow}
                onChange={(e) => setTimeWindow(e.target.value)}
                className="appearance-none bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg pl-3 pr-7 py-1.5 cursor-pointer"
              >
                <option value="15m">Last 15m</option>
                <option value="1h">Last 1h</option>
                <option value="6h">Last 6h</option>
                <option value="24h">Last 24h</option>
                <option value="all">All time</option>
              </select>
              <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
            </div>

            {/* Full-text search */}
            <div className="relative flex-1 min-w-[160px]">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doSearch(searchQuery)}
                placeholder="Search across filtered logs… (Enter for server search)"
                className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg pl-9 pr-3 py-1.5 placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Live / Pause toggle */}
            <button
              onClick={() => setLive((v) => !v)}
              disabled={!selectedFile}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 ${
                live
                  ? 'bg-green-950 text-green-400 border-green-800'
                  : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
              }`}
              title={live ? 'Pause live tail' : 'Start live tail'}
            >
              {live ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  Live
                </>
              ) : (
                <>
                  <PlayIcon className="w-3.5 h-3.5" />
                  Live
                </>
              )}
            </button>

            {/* Autoscroll toggle */}
            <button
              onClick={() => setAutoScroll((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                autoScroll ? 'bg-slate-800 text-slate-200' : 'bg-slate-800 text-slate-500 hover:text-slate-300'
              }`}
              title="Toggle auto-scroll"
            >
              {autoScroll ? <PauseIcon className="w-3.5 h-3.5" /> : <PlayIcon className="w-3.5 h-3.5" />}
              Autoscroll
            </button>

            {/* Export */}
            {displayEntries.length > 0 && (
              <button
                onClick={exportLines}
                title="Download displayed log lines"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold transition-colors"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                Export
              </button>
            )}
          </div>

          {/* Console */}
          <div className="flex-1 overflow-y-auto font-mono text-xs min-h-0 max-h-[62vh]">
            {loading && !displayEntries.length && (
              <div className="flex items-center justify-center h-32 text-slate-500">
                <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                Loading…
              </div>
            )}

            {!loading && !displayEntries.length && selectedFile && (
              <div className="text-center py-12 text-slate-500">
                {activeSearch.trim() ? 'No matches for this search + filters.' : 'No log lines match the current filters.'}
              </div>
            )}

            {!selectedFile && <div className="text-center py-12 text-slate-500">Select a log file from the sidebar.</div>}

            {displayEntries.map((line, i) => {
              const isOpen = expanded.has(i)
              const lvlText = LEVEL_TEXT[line.level] || LEVEL_TEXT.info
              return (
                <div key={i}>
                  <div
                    onClick={() => toggleExpand(i)}
                    className="flex gap-2 items-baseline px-3 py-1.5 border-b border-slate-800/60 hover:bg-slate-800/50 cursor-pointer"
                  >
                    <span className="text-slate-500 whitespace-nowrap">{fmtTime(line.timestamp)}</span>
                    <span className={`font-bold w-11 flex-none uppercase ${lvlText}`}>{line.level}</span>
                    <span className="text-violet-400 whitespace-nowrap">{line.context ? `[${line.context}]` : ''}</span>
                    <span className="text-slate-300 truncate flex-1">{line.message || line.raw}</span>
                    <span className="text-slate-600">{isOpen ? '▾' : '▸'}</span>
                  </div>

                  {isOpen && (
                    <div className="bg-slate-950 border-b border-slate-800/60 px-4 py-3 pl-12">
                      {line.data && Object.keys(line.data).length > 0 ? (
                        <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-0.5 text-[11.5px]">
                          {Object.entries(line.data).map(([k, v]) => (
                            <div key={k} className="contents">
                              <span className="text-slate-500">{k}</span>
                              <span className="text-slate-300 break-all">
                                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[11.5px] text-slate-500">No structured payload.</div>
                      )}

                      {/* Full message when truncated in the row */}
                      {line.message && (
                        <div className="mt-2 text-[11.5px] text-slate-400 break-all whitespace-pre-wrap">{line.message}</div>
                      )}

                      {line.stack && (
                        <pre className="mt-2 bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2 text-[11px] text-red-300 whitespace-pre-wrap break-all overflow-x-auto">
                          {line.stack}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
            <div ref={logEndRef} />
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-slate-800 flex items-center justify-between text-xs text-slate-500">
            <span>{autoScroll ? '▲ newest · auto-scroll on' : 'auto-scroll off'}</span>
            <span>
              {displayEntries.length.toLocaleString()} line{displayEntries.length !== 1 ? 's' : ''} · {windowCounts.err} ERROR ·{' '}
              {windowCounts.warn} WARN in window
              {selectedFile && <span className="font-mono ml-2">{selectedFile}</span>}
            </span>
          </div>
        </div>
      </div>

      {searching && <div className="text-xs text-slate-500">Searching…</div>}
    </div>
  )
}
